import { useState } from "react";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/daiobi3oh653ivfvohhg";
const MESSAGE_LIMIT = 500;

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

const feedbackTypes = ["内容更正", "内容建议", "线索提供", "合作洽谈"];

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    if (message.trim().length > MESSAGE_LIMIT) {
      setStatus("error");
      setFormError(`反馈内容不能超过 ${MESSAGE_LIMIT} 字，请精简后再提交。`);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      const text = String(value).trim();
      if (!text) return;
      payload.append(key, text);
    });

    setLoading(true);
    setFormError("");
    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk) {
        setStatus("success");
        setMessage("");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      id="feedback-form-51cg"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-sm text-foreground-700">称呼</span>
          <input
            type="text"
            name="name"
            autoComplete="name"
            placeholder="怎么称呼你"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 placeholder:text-foreground-500"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm text-foreground-700">邮箱</span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="方便我们回复你"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 placeholder:text-foreground-500"
          />
        </label>
      </div>

      <label className="block">
        <span className="mb-1.5 block text-sm text-foreground-700">反馈类型</span>
        <select
          name="type"
          className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none focus:border-primary-400"
        >
          {feedbackTypes.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="mb-1.5 flex items-center justify-between text-sm text-foreground-700">
          <span>反馈内容</span>
          <span className={message.length > MESSAGE_LIMIT ? "text-primary-600" : "text-foreground-500"}>
            {message.length}/{MESSAGE_LIMIT}
          </span>
        </span>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={MESSAGE_LIMIT}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="请尽量写明具体条目与可核实的信息来源，方便我们跟进。"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition focus:border-primary-400 placeholder:text-foreground-500"
        />
      </label>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-aux-note"
      />

      <div className="flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={loading}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? "提交中…" : "提交反馈"}
        </button>
        {status === "success" && (
          <span className="flex items-center gap-1.5 text-sm text-secondary-700">
            <i className="ri-checkbox-circle-fill" />
            已收到，我们会尽快核实并回复。
          </span>
        )}
        {status === "error" && (
          <span className="flex items-center gap-1.5 text-sm text-primary-600">
            <i className="ri-error-warning-fill" />
            {formError}
          </span>
        )}
      </div>
    </form>
  );
}